import type { MouseEvent } from 'react';
import type { Theme } from '@mui/material/styles';

import { useReducedMotion } from 'framer-motion';

import { Box, ButtonBase } from '@mui/material';

import { FeatureBadge } from '../feature-badge';
import { HouseIcon } from '../../assets/icons';
import { motion, pressSx } from '../../theme/motion';

// ----------------------------------------------------------------------

export type TopNavTab = {
  label: string;
  path: string;
  /** Drawn after the label, e.g. how many items the tab holds. */
  count?: number;
  /** Stage text after the label, e.g. "Preview". */
  badge?: string;
  'data-testid'?: string;
};

export type TopNavTabsProps = {
  tabs: TopNavTab[];
  activePath: string;
  onNavigate: (path: string) => void;
  homeLabel: string;
  navLabel?: string;
};

/** A plain click navigates in place. A modified or middle click opens the link as the browser would. */
function navigateOnPlainClick(event: MouseEvent, path: string, onNavigate: (path: string) => void) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  onNavigate(path);
}

/** Spaces out the count and badge, which the browser would run into the label. */
function tabAriaLabel({ label, count, badge }: TopNavTab): string | undefined {
  if (count === undefined && !badge) return undefined;
  return [label, count, badge].filter((part) => part !== undefined && part !== '').join(' ');
}

export function isTabActive(path: string, activePath: string): boolean {
  return path === '/' ? activePath === '/' : activePath.startsWith(path);
}

/** The 36px round button of the bar. Also for round actions beside it. */
export const topNavRoundButtonSx = (theme: Theme, reduceMotion: boolean) => ({
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey[100],
  bgcolor: theme.palette.grey[800],
  '&:hover': { bgcolor: theme.palette.grey[700] },
  // Extend the 36px visual to a 40px touch target.
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 40,
    height: 40,
    transform: 'translate(-50%, -50%)',
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.light}`,
    outlineOffset: 2,
  },
  ...pressSx(reduceMotion),
  transition: `background-color ${motion.duration.fast} ${motion.ease.out}, transform ${motion.duration.instant} ${motion.ease.out}`,
});

/** The round home button and the pill tab bar. */
export function TopNavTabs({ tabs, activePath, onNavigate, homeLabel, navLabel }: TopNavTabsProps) {
  const reduceMotion = useReducedMotion() ?? false;

  return (
    <Box
      component="nav"
      aria-label={navLabel}
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        pt: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 0 },
      }}
    >
      <ButtonBase
        href="/"
        aria-label={homeLabel}
        onClick={(event: MouseEvent) => navigateOnPlainClick(event, '/', onNavigate)}
        sx={(theme) => topNavRoundButtonSx(theme, reduceMotion)}
      >
        <HouseIcon sx={{ width: 17, height: 18 }} />
      </ButtonBase>

      {/* Scrolls sideways on narrow screens. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '10px',
          height: 36,
          px: 0.375,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {tabs.map((tab) => {
          const active = isTabActive(tab.path, activePath);

          return (
            <ButtonBase
              key={tab.path}
              href={tab.path}
              data-testid={tab['data-testid']}
              aria-current={active ? 'page' : undefined}
              aria-label={tabAriaLabel(tab)}
              onClick={(event: MouseEvent) => navigateOnPlainClick(event, tab.path, onNavigate)}
              sx={(theme) => ({
                height: 30,
                px: 1.5,
                // Concentric with the 10px container minus its ~3px inset.
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                color: active ? theme.palette.common.white : theme.palette.grey[400],
                fontSize: 14,
                fontWeight: 700,
                ...(active && {
                  bgcolor: theme.palette.grey[800],
                }),
                '&:hover': {
                  bgcolor: active ? theme.palette.grey[800] : 'rgba(255, 255, 255, 0.04)',
                },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${theme.palette.primary.light}`,
                  outlineOffset: 1,
                },
                ...pressSx(reduceMotion),
                transition: `background-color ${motion.duration.fast} ${motion.ease.out}, color ${motion.duration.fast} ${motion.ease.out}, transform ${motion.duration.instant} ${motion.ease.out}`,
              })}
            >
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                {tab.label}
                {tab.count !== undefined && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: active ? 'grey.400' : 'grey.600',
                    }}
                  >
                    {tab.count}
                  </Box>
                )}
                {/* The card ratio: a 10px badge on a 16px title. */}
                {tab.badge && <FeatureBadge label={tab.badge} sx={{ ml: 0.75, fontSize: 9 }} />}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
